/**
 * Pure display helpers for the shared Game Rules modal.
 */
import { cardBonusScheduleInfo, fogOfWarOf, gameConfigWithDefaults, type GameConfig, type GameResponse, type MapDef } from '@warcarp/api-types'
import { teamsLobbyLabel, teamsShortLabel } from './teamSetup'

/**
 * The rules a lobby game will actually play with: the host's staged setup,
 * overridden by anything the map pins (fixed setups), over the defaults.
 * Shared by the lobby card, the host's Edit Game button and the rules modal.
 */
export function effectiveLobbyConfig(game: Pick<GameResponse, 'setupConfig'>, map: Pick<MapDef, 'fixedSetup'> | null | undefined): GameConfig {
  return gameConfigWithDefaults({ ...game.setupConfig, ...map?.fixedSetup?.config })
}

/**
 * Value for the "Players" row of the rules table.
 *
 * LOBBY games are still filling seats, so the meaningful number is the
 * allowed range (min–max). Once the game is live the roster is locked —
 * showing the config range would be misleading, so we show the actual
 * number of players in THIS game instead.
 *
 * Uses an en dash (–) between min and max to match the table styling.
 */
export function playersRuleValue(
  game: Pick<GameResponse, 'status' | 'minPlayers' | 'maxPlayers' | 'players'>,
): string {
  if (game.status === 'LOBBY') return game.minPlayers === game.maxPlayers ? String(game.maxPlayers) : `${game.minPlayers}–${game.maxPlayers}`
  return String(game.players.length)
}

/**
 * One-line lobby rules summary: the few settings that most change how a game
 * plays, shown beside the "View rules" link so a player can judge the game
 * without opening the full table. Order: cards, placement, starting troops.
 *
 * A team game's format ("3v3 · pick seats", or "Teams of 3 · pick your
 * seat" without a seat count) is the FIRST part, since it changes the game
 * more than anything else here.
 */
export function lobbyRulesSummary(
  config: Pick<GameConfig, 'cardBonusSchedule' | 'autoAssignTerritories' | 'startingTroops' | 'fogOfWar' | 'teams'>,
  maxPlayers?: number,
): string {
  // Teams, then fog, change the game more than anything else here, so they
  // lead; free-for-all games' summaries are unchanged.
  const fog = fogOfWarOf(config)
  const teams = config.teams
  return [
    ...(teams ? [maxPlayers ? teamsShortLabel(teams, maxPlayers) : teamsLobbyLabel(teams)] : []),
    ...(fog === 'none' ? [] : [fog === 'light' ? 'Light fog' : 'Heavy fog']),
    `${cardBonusScheduleInfo(config.cardBonusSchedule).label} cards`,
    config.autoAssignTerritories ? 'Random placement' : 'Manual claim',
    `${config.startingTroops} ${config.startingTroops === 1 ? 'troop' : 'troops'} per territory`,
  ].join(' · ')
}
